import React, { ReactNode } from 'react';
import { Popover } from 'antd';
import { CSSTransition } from 'react-transition-group';

import NotificationDialog from '~src/shared/dialogs/notificationDialog/NotificationDialog';
import ActionConfirmDialog from '~src/shared/dialogs/actionConfirmDialog/ActionConfirmDialog';
import ThreeColsHeader from '~src/shared/headers/threeColsHeader/ThreeColsHeader';
import NavArrowLnk from '~src/shared/buttons/NavArrowLnk';
import { KeyTextI } from '~src/interfaces/translate.interface';
import {ApiReduxResp, isError} from '~src/services/apiMiddleware';
import IconTextItem from '~src/shared/IconTextItem';
import { txtOrKey } from '~src/utils';

import { MenuItemConfI } from './types';
import * as SC from './itemDetailsHeader.sc';
import {TFunction} from "i18next";

interface DefaultProps {
  children: ReactNode;
  isEditFrmVisible: boolean;
  deletionTitleKey: string | null;
  deletionContentKey: string | null;
  deletionError: string | null;
  loadingDel: boolean;
  isFramework: boolean;
  toggleEditFrm(isShow: boolean): void;
}

interface OwnProps extends DefaultProps {
  t: TFunction;
  backArrow?: KeyTextI;
  supTitle?: KeyTextI;
  title?: KeyTextI;
  description?: KeyTextI;
  framework?: KeyTextI;
  frameworkTxt?: KeyTextI;
  menuEdit?: KeyTextI;
  menuDel?: KeyTextI;
  dialogTitle?: KeyTextI;
  dialogDesc?: KeyTextI;
  additionalMenuItems?: MenuItemConfI[];
  deleteItem(): ApiReduxResp<unknown>;
  onBackClick?(): void;
}

type Props = OwnProps;

interface State {
  confirmDeletion: boolean;
  editMenuVisible: boolean;
  showDelErrModal: boolean;
  shouldAnimateEditForm: boolean;
}

class ItemDetailsHeader extends React.Component<Props, State> {
  static defaultProps: DefaultProps = {
    children: null,
    deletionTitleKey: null,
    deletionContentKey: null,
    deletionError: null,
    loadingDel: false,
    toggleEditFrm: () => '',
    isFramework: false,
    isEditFrmVisible: false,
  };

  state = {
    confirmDeletion: false,
    editMenuVisible: false,
    showDelErrModal: false,
    shouldAnimateEditForm: true,
  };

  formAnimationTimeout = 500;

  onDeleteItem = () => {
    void this.props.deleteItem().then((resp) => {
      if (isError(resp) && resp.error.response) {
        if (resp.error.response && resp.error.response.data.some(err => err.translation_key === 'err_delete_referenced')) {
          this.toggleConfirmDeletionModal();
          this.toggleErrorModal(true);
        }
      }
    });
  };

  toggleConfirmDeletionModal = () => {
    this.setState((prevState) => ({ confirmDeletion: !prevState.confirmDeletion }));
  };

  editMenuVisibility = (editMenuVisible = false) => {
    this.setState({ editMenuVisible });
  };

  toggleErrorModal = (show = false) => {
    this.setState({ showDelErrModal: typeof show === 'boolean' ? show : false });
  };

  onEditClick = () => {
    this.setState({ shouldAnimateEditForm: false });
    this.props.toggleEditFrm(true);
  };

  onDelClick = () => {
    this.toggleConfirmDeletionModal();
  };

  onMenuClick = () => {
    this.editMenuVisibility();
  };

  renderMenuItems = (): ReactNode => {
    const { t, menuEdit, menuDel, framework, frameworkTxt, isFramework, additionalMenuItems } = this.props;
    return (
      <>
        {menuEdit && (
          <SC.MenuItem onClick={this.onEditClick}>
            <IconTextItem iconName="iconPen" labelKey={menuEdit.key} label={txtOrKey(menuEdit, t)} />
          </SC.MenuItem>
        )}
        {additionalMenuItems &&
          additionalMenuItems.map((item, i) => (
            <SC.MenuItem key={i} onClick={item.onClick}>
              <IconTextItem
                iconName={item.iconName}
                labelKey={item.txtKey}
                label={item.txt || t(item.txtKey)}
              />
            </SC.MenuItem>
          ))}
        {isFramework && framework && frameworkTxt ? (
          <SC.NoDeletionMessage>
            <SC.FrameworkBox data-translation={framework.key}>{framework.txt}</SC.FrameworkBox>
            <span data-translation={frameworkTxt.key}>{txtOrKey(frameworkTxt, t)}</span>
          </SC.NoDeletionMessage>
        ) : (
          menuDel && (
            <SC.MenuItemDel onClick={this.onDelClick}>
              <IconTextItem iconName="iconBin" labelKey={menuDel.key} label={txtOrKey(menuDel, t)} />
            </SC.MenuItemDel>
          )
        )}
      </>
    );
  };

  render() {
    const { t, backArrow, dialogTitle, dialogDesc } = this.props;

    return (
      <ThreeColsHeader
        supTitle={this.props.supTitle}
        title={this.props.title}
        description={this.props.description}
        leftColChildren={
          backArrow && (
            <div onClick={this.props.onBackClick}>
              <NavArrowLnk>
                <span style={{ whiteSpace: 'nowrap' }} data-translation={backArrow.key}>
                  {txtOrKey(backArrow, t)}
                </span>
              </NavArrowLnk>
            </div>
          )
        }
        rightColChildren={
          <>
            <Popover
              content={
                <SC.ItemMenuContainer onClick={this.onMenuClick}>
                  {this.renderMenuItems()}
                </SC.ItemMenuContainer>
              }
              arrowPointAtCenter
              trigger="click"
              placement="bottomRight"
              open={this.state.editMenuVisible}
              onOpenChange={this.editMenuVisibility}
            >
              <div style={{ marginRight: 15 }}>
                <SC.MenuBtn name="iconSettings" />
              </div>
            </Popover>
          </>
        }
      >
        <CSSTransition in={this.props.isEditFrmVisible} timeout={this.formAnimationTimeout} unmountOnExit>
          <SC.EditFormContainer
            $timeout={this.formAnimationTimeout}
            $shouldAnimate={this.state.shouldAnimateEditForm}
          >
            <div style={{ paddingBottom: 30 }}>{this.props.children}</div>
          </SC.EditFormContainer>
        </CSSTransition>
        <ActionConfirmDialog
          t={t}
          show={this.state.confirmDeletion}
          close
          onHide={this.toggleConfirmDeletionModal}
          actionTitleKey={this.props.deletionTitleKey}
          actionContentKey={this.props.deletionContentKey}
          actionCall={this.onDeleteItem}
          showDeletion
          loadingAction={this.props.loadingDel}
          actionError={this.props.deletionError}
        />
        <NotificationDialog
          t={t}
          title={dialogTitle}
          descr={dialogDesc}
          show={this.state.showDelErrModal}
          close
          onHide={this.toggleErrorModal}
        />
      </ThreeColsHeader>
    );
  }
}

export default ItemDetailsHeader;
